import { useState } from 'react';
import { DateOnlyPicker } from '@/shared/components/DateTimePicker.tsx';

/* ── types ────────────────────────────────────────────── */

export interface PeriodValue {
  mode: 'month' | 'range';
  month?: string;
  from?: string;
  to?: string;
}

interface Props {
  value: PeriodValue;
  onChange: (v: PeriodValue) => void;
  modes?: ('month' | 'range')[];
  availableMonths?: string[];
  error?: string;
}

/* ── helpers ──────────────────────────────────────────── */

function generateMonthOptions(): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    options.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
  }
  return options;
}

export function validatePeriodMax12Months(v: PeriodValue): string | null {
  if (v.mode === 'range' && v.from && v.to) {
    const fromDate = new Date(v.from);
    const toDate = new Date(v.to);
    if (toDate <= fromDate) return 'Data final deve ser posterior à inicial';
    const diffMs = toDate.getTime() - fromDate.getTime();
    const diffMonths = diffMs / (1000 * 60 * 60 * 24 * 30.44);
    if (diffMonths > 12) return 'Período máximo de 12 meses';
  }
  return null;
}

const DEFAULT_MONTH_OPTIONS = generateMonthOptions();

function formatMonthLabel(value: string): string {
  const [year, month] = value.split('-').map(Number);
  const d = new Date(year, month - 1, 1);
  const label = d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/* ── component ────────────────────────────────────────── */

export function PeriodSelector({ value, onChange, modes = ['month', 'range'], availableMonths, error }: Props) {
  const MONTH_OPTIONS = availableMonths
    ? availableMonths.map((v) => ({ value: v, label: formatMonthLabel(v) }))
    : DEFAULT_MONTH_OPTIONS;
  const hasMonth = modes.includes('month');
  const hasRange = modes.includes('range');
  const [localError] = useState('');

  return (
    <div className="relative space-y-2">
      {/* Mode toggle (only if both modes available) */}
      {hasMonth && hasRange && (
        <div className="flex gap-1 rounded-md border border-gray-300 p-0.5 dark:border-gray-600">
          <button
            type="button"
            onClick={() => onChange({ mode: 'month', month: value.month || MONTH_OPTIONS[0]?.value })}
            className={`rounded px-3 py-1 text-xs font-medium transition-colors ${
              value.mode === 'month'
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:text-gray-800 dark:text-gray-400'
            }`}
          >
            Mensal
          </button>
          <button
            type="button"
            onClick={() => onChange({ mode: 'range', from: value.from, to: value.to })}
            className={`rounded px-3 py-1 text-xs font-medium transition-colors ${
              value.mode === 'range'
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:text-gray-800 dark:text-gray-400'
            }`}
          >
            Intervalo
          </button>
        </div>
      )}

      {/* Month mode */}
      {value.mode === 'month' && hasMonth && (
        <select
          value={value.month || ''}
          onChange={(e) => onChange({ ...value, month: e.target.value })}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
        >
          {MONTH_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      )}

      {/* Range mode */}
      {value.mode === 'range' && hasRange && (
        <div className="flex items-center gap-2">
          <DateOnlyPicker
            value={value.from ? new Date(value.from + 'T00:00:00') : null}
            onChange={(d) => onChange({ ...value, from: d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : undefined })}
            maxDate={value.to ? new Date(value.to + 'T00:00:00') : undefined}
            placeholder="Data inicial"
          />
          <span className="text-xs text-gray-400">até</span>
          <DateOnlyPicker
            value={value.to ? new Date(value.to + 'T00:00:00') : null}
            onChange={(d) => onChange({ ...value, to: d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : undefined })}
            minDate={value.from ? new Date(value.from + 'T00:00:00') : undefined}
            placeholder="Data final"
          />
        </div>
      )}

      {(error || localError) && (
        <p className="absolute -bottom-5 left-0 text-xs text-red-500">{error || localError}</p>
      )}
    </div>
  );
}
